'use client';

import * as m from 'motion/react-m';
import { ComponentProps, PropsWithChildren, ReactNode } from 'react';

import { Styles, css } from '@flipster/styled-system/css';
import { center, flex, hstack, stack } from '@flipster/styled-system/patterns';

import { Portal } from '../../misc/portal';
import { Button as RawButton } from '../button';
import { Spinner } from '../spinner';

const initial = { opacity: 0.5 };
const animate = { opacity: 1, transition: { ease: 'easeOut' as const, duration: 0.2 } };
const exit = { opacity: 0.5, transition: { ease: 'easeIn' as const, duration: 0.2 } };

interface DialogProps extends PropsWithChildren {
  css?: Styles;
}

const Root = ({ children, css: cssProps }: DialogProps) => {
  return (
    <Portal selector='#alert'>
      <m.div
        key='backdrop'
        role='presentation'
        className={css({ zIndex: 'modal', pos: 'fixed', inset: 0, bgColor: 'background.interaction.dim' })}
        initial={initial}
        animate={animate}
        exit={exit}
      />
      <m.aside
        key='dialog'
        role='dialog'
        aria-modal='true'
        className={css(
          stack.raw({
            zIndex: 'modal',
            pos: 'fixed',
            top: '50%',
            left: '50%',
            w: 'calc(100% - 48px)',
            maxW: 480,
            pt: { base: 24, tablet: 32 },
            px: { base: 16, tablet: 32 },
            pb: { base: 16, tablet: 32 },
            rounded: 12,
            bgColor: 'background.default.level-1',
            border: '1px solid',
            borderColor: 'border.default.level-1',
            boxShadow: '0 8px 40px 0 rgba(0, 0, 0, 0.40)',
            pointerEvents: 'auto',
          }),
          cssProps,
        )}
        initial={{ ...initial, scale: 0.8 }}
        animate={{ ...animate, scale: 1 }}
        exit={{ ...exit, scale: 0.8 }}
        transformTemplate={(_, generatedTransforms) => `translate(-50%, -50%) ${generatedTransforms}`}
      >
        <div className={stack({ gap: 0 })}>{children}</div>
      </m.aside>
    </Portal>
  );
};

const Title = ({ leftAddon, label, css: cssProps }: { leftAddon?: ReactNode; label: ReactNode; css?: Styles }) => {
  return (
    <header className={css(hstack.raw({ gap: 8, pb: 16 }), cssProps)}>
      {leftAddon && <div className={center({ flexShrink: 0 })}>{leftAddon}</div>}
      <div className={css({ textStyle: 'Title/S', color: 'content.default.level-4' })}>{label}</div>
    </header>
  );
};

const Content = ({ children, isLoading, css: cssProps }: PropsWithChildren<{ isLoading?: boolean; css?: Styles }>) => {
  if (isLoading) {
    return (
      <div className={css(center.raw({ py: 32 }), cssProps)}>
        <Spinner size={24} />
      </div>
    );
  }
  return (
    <div
      className={css(
        { textStyle: 'Text/L/Regular', color: 'content.default.level-3', pb: 24, whiteSpace: 'pre-line' },
        cssProps,
      )}
    >
      {children}
    </div>
  );
};

const Footer = ({
  direction = 'horizontal',
  addon,
  children,
  css: cssProps,
}: PropsWithChildren<{ direction?: 'vertical' | 'horizontal'; addon?: ReactNode; css?: Styles }>) => {
  return (
    <footer className={css({ w: '100%' })}>
      {addon}
      <div
        className={css(
          flex.raw({
            flexDirection: direction === 'vertical' ? 'column-reverse' : 'row',
            gap: 8,
            w: '100%',
          }),
          cssProps,
        )}
      >
        {children}
      </div>
    </footer>
  );
};

const Button = ({ css: cssProps, ...props }: ComponentProps<typeof RawButton>) => {
  return <RawButton size='large' css={css.raw({ w: '100%' }, cssProps)} {...props} />;
};

Root.displayName = 'Dialog.Root';
Title.displayName = 'Dialog.Title';
Content.displayName = 'Dialog.Content';
Footer.displayName = 'Dialog.Footer';
Button.displayName = 'Dialog.Button';

export { Root, Title, Content, Footer, Button };
